/**
 * Shared TypeScript types for Hyperliquid integration
 */

// ============================================================================
// Market Types
// ============================================================================

export enum MarketType {
  PERP = "PERP",
  SPOT = "SPOT",
}

export enum MarketCategory {
  ALL = "all",
  FAVORITES = "favorites",
  PERP = "perp",
  SPOT = "spot",
  DEFI = "defi",
  LAYER1 = "layer1",
  LAYER2 = "layer2",
  MEME = "meme",
  AI = "ai",
  GAMING = "gaming",
}

// ============================================================================
// Unified Market Interface
// ============================================================================

export interface HyperliquidMarket {
  // Common fields
  type: MarketType;
  symbol: string; // "BTC-PERP" or "PURR/USDC"
  baseAsset: string; // "BTC" or "PURR"
  quoteAsset: string; // "USD" or "USDC"

  // Market data
  markPrice?: string;
  midPrice?: string;
  volume24h?: string;
  priceChange24h?: number; // Percentage
  prevDayPrice?: string;

  // Perp-specific
  funding?: string;
  openInterest?: string;
  maxLeverage?: number;
  szDecimals?: number;
  onlyIsolated?: boolean;
  isDelisted?: boolean;

  // Spot-specific
  tokens?: [number, number]; // [baseTokenIndex, quoteTokenIndex]
  index?: number;
  isCanonical?: boolean;

  // Fee info (HIP-3 specific)
  /** Whether this market has HIP-3 growth mode enabled (~90% fee reduction). */
  isGrowthMode?: boolean;

  // UI helpers
  searchString: string; // Lowercase for search: "btcperp" or "purrusdc"
  categories: MarketCategory[];
}

// ============================================================================
// API Response Types - Perpetuals
// ============================================================================

export interface PerpAssetMeta {
  name: string;
  szDecimals: number;
  maxLeverage: number;
  onlyIsolated?: boolean;
  isDelisted?: boolean;
  /** HIP-3 growth mode status. Present and "enabled" for growth-mode markets, absent for standard-mode. */
  growthMode?: string;
}

export interface PerpAssetContext {
  dayNtlVlm: string; // 24h volume
  funding: string;
  impactPxs: [string, string];
  markPx: string;
  midPx: string;
  openInterest: string;
  oraclePx: string;
  premium: string;
  prevDayPx: string;
}

export interface PerpMetaAndAssetCtxsResponse {
  universe: PerpAssetMeta[];
}

// ============================================================================
// API Response Types - Spot
// ============================================================================

export interface SpotToken {
  name: string;
  szDecimals: number;
  weiDecimals: number;
  index: number;
  tokenId: string;
  isCanonical: boolean;
  evmContract: string | null;
  fullName: string | null;
}

export interface SpotUniverse {
  name: string; // "PURR/USDC"
  tokens: [number, number]; // [baseTokenIndex, quoteTokenIndex]
  index: number;
  isCanonical: boolean;
}

export interface SpotMeta {
  tokens: SpotToken[];
  universe: SpotUniverse[];
}

export interface SpotAssetContext {
  dayNtlVlm: string;
  markPx: string;
  midPx: string;
  prevDayPx: string;
}

export interface SpotMetaAndAssetCtxsResponse {
  0: SpotMeta;
  1: SpotAssetContext[];
}

// ============================================================================
// WebSocket Types
// ============================================================================

export interface AllMidsUpdate {
  [symbol: string]: string; // { "BTC": "67234.50", "ETH": "3456.20", ... }
}

// ============================================================================
// Market Selector Types
// ============================================================================

export type SortBy = "volume" | "name" | "change" | "price";
export type SortDirection = "asc" | "desc";

export interface MarketFilters {
  marketType: MarketType | "ALL";
  category: MarketCategory;
  searchQuery: string;
  sortBy: SortBy;
  sortDirection: SortDirection;
}

export interface FavoritesState {
  favorites: Set<string>; // Set of market symbols
  toggleFavorite: (symbol: string) => void;
  isFavorite: (symbol: string) => boolean;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the correct coin identifier for Hyperliquid API calls
 *
 * For perpetuals: Use the base asset name directly (e.g., "BTC", "ETH")
 * For spot: Use the @{index} format (e.g., "@0", "@107")
 *
 * This is required because Hyperliquid's API uses different coin identifiers
 * for spot vs perpetual markets in l2Book, candleSnapshot, and WebSocket subscriptions.
 *
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/asset-ids
 */
export const getHyperliquidCoinId = (market: HyperliquidMarket): string => {
  if (market.type === MarketType.SPOT && market.index !== undefined) {
    return `@${market.index}`;
  }
  return market.baseAsset;
};
